import type { MpmNumericSettingChange } from "../mpm/mpmNumericSettingsChanges";
import type {
  PlayLogAmplifierSummary,
  PlayLogHovSkillSummary,
  PlayLogMpSummary,
  PlayLogWallSummary,
} from "./playLogBalanceSummary";
import type { WallRewardEffect } from "@/features/hoverIdle/wall/wallChallengePolicy";
import {
  getWallFirstClearReward,
  isWallId,
  type WallFirstClearReward,
} from "@/features/hoverIdle/wall/wallDefinitions";
import {
  compareHuge,
  deserializeHuge,
  huge,
  hugeZero,
  serializeHuge,
} from "@/shared/numbers";
import type {
  PlayLogEntry,
  PlayLogEvent,
  PlayLogMHovExecuteSource,
  PlayLogSample,
  PlayLogSession,
  PlayLogSessionEndEntry,
  PlayLogSnapshotSummary,
} from "./playLogTypes";

// Compact PlayLog is a balance-confirmation lane derived from the detailed
// PlayLog stream. It is intentionally aggressive about dropping high-volume
// developer-only entries (`upgrade-milestone-purchase`,
// `upgrade-milestone-purchase-batch`, `reset-preview-probe`). Automation bursts
// feed OHov-cycle aggregates without exporting their per-burst state or trace.

const COMPACT_EVENT_PASS_THROUGH = new Set<string>([
  "mhov-execute",
  "nhov-execute",
  "ohov-execute",
  "ohov-auto-execute",
  "achievement-unlock",
  "speed-change",
  "mpm-numeric-settings-change",
  "offline-reward-apply",
]);

export interface PlayLogCompactHovSkillSummary {
  readonly acquiredCount: number;
  readonly acquiredIds: readonly string[];
  readonly hov1GainMultiplierLog10: number | null;
  readonly hovGainPerSecondLog10: number | null;
  readonly timGainPerSecondLog10: number | null;
  readonly mpCapacityBonus: string;
  readonly amplifierUnlocked: boolean;
  readonly amplifierPanelMpPlacementUnlocked: boolean;
  readonly mhovMpExecutionUnlocked: boolean;
  readonly nhovMpExecutionAssignmentUnlocked: boolean;
  readonly ohovHovCoreGainMultiplierUnlocked: boolean;
  readonly mpCapacityCoreOHovHovCoreGainMultiplierUnlocked: boolean;
  readonly freeMpCoreOHovHovCoreGainMultiplierUnlocked: boolean;
  readonly wallUnlocked: boolean;
  readonly explorationUnlocked: boolean;
  readonly wallRewardCapRelaxed: boolean;
}

// `available` follows the Hov skill resolved effect `amplifierUnlocked` and is
// the authoritative "Amplifier is unlocked for the player" signal. `statePresent`
// only reports whether the Amplifier section was readable from the model
// snapshot, which does not imply player-level availability.
export interface PlayLogCompactAmplifierEntrySummary {
  readonly id: string;
  readonly available: boolean;
  readonly statePresent: boolean;
  readonly gaugeValue: number | null;
  readonly burnState: string | null;
  readonly amplifierGainMultiplierLog10: number | null;
  readonly activeGaugeOutputGainLog10: number | null;
}

export interface PlayLogCompactAmplifierSummary {
  readonly available: boolean;
  readonly statePresent: boolean;
  readonly ap: string | null;
  readonly apLog10: number | null;
  readonly apExponent: number | null;
  readonly hgMultiplier: string | null;
  readonly hgMultiplierLog10: number | null;
  readonly availableEntryIds: readonly string[];
  readonly activeEntryIds: readonly string[];
  readonly entries: readonly PlayLogCompactAmplifierEntrySummary[];
}

export interface PlayLogCompactWallEntrySummary {
  readonly id: string;
  readonly bestClearMs: number | null;
  // Authoritative typed reward effect mirror. See
  // `PlayLogWallEntrySummary.rewardEffect` for the contract — add-effect walls
  // keep their additive value here so compact balance analysis does not lose
  // the bonus to the multiplier projection.
  readonly rewardEffect: WallRewardEffect;
  readonly rewardMultiplier: number;
}

export interface PlayLogCompactWallSummary {
  readonly unlocked: boolean;
  readonly activeWallId: string | null;
  readonly activeRunElapsedMs: number | null;
  readonly clearedWallIds: readonly string[];
  readonly walls: readonly PlayLogCompactWallEntrySummary[];
}

export interface PlayLogCompactMpAssignmentCountsSummary {
  readonly hovTargets: number;
  readonly hovGainUpgrades: number;
  readonly amplifierPanels: number;
  readonly amplifierUpgrades: number;
  readonly mhovExecutionMpCost: number;
  readonly nhovExecutionAssignments: number;
  readonly nhovExecutionMpCost: number;
  readonly ohovExecutionMpCost: number;
}

export interface PlayLogCompactMpSummary {
  readonly numericSettings?: PlayLogMpSummary["numericSettings"];
  readonly capacity: number;
  readonly assignedTotal: number;
  readonly available: number;
  readonly assignments: PlayLogCompactMpAssignmentCountsSummary;
  readonly hovTargetCellIndices: readonly number[];
  readonly hovGainUpgradeTargets: readonly string[];
  readonly amplifierPanelEntryIds: readonly string[];
  readonly amplifierUpgradeEntryIds: readonly string[];
  readonly nhovExecutionBranchIds: readonly string[];
}

export interface PlayLogCompactBalanceState {
  readonly oHovCount?: number;
  readonly hovLog10?: number | null;
  readonly hovGainPerSecondLog10?: number | null;
  readonly tim?: string;
  readonly mHovCount?: number;
  readonly mHovMultiplier?: string;
  readonly mHovMultiplierLog10?: number | null;
  readonly nHovCount?: number;
  readonly nHovBranchLevels?: Record<string, number>;
  readonly hovCore?: string;
  readonly hovCoreLog10?: number | null;
  readonly hgEnhancementLevel?: number;
  readonly h9gEnhancementLevel?: number;
  readonly hgMilestoneCount?: number;
  readonly h9gMilestoneCount?: number;
  readonly hovSkill?: PlayLogCompactHovSkillSummary;
  readonly amplifier?: PlayLogCompactAmplifierSummary;
  readonly wall?: PlayLogCompactWallSummary;
  readonly mp?: PlayLogCompactMpSummary;
}

export interface PlayLogCompactTimedFields {
  readonly runId: string;
  readonly seq: number;
  readonly wallTimeMs: number;
  readonly gameTimeMs: number;
  readonly speedMultiplier: number;
}

export interface PlayLogCompactCommonFields
  extends PlayLogCompactTimedFields, PlayLogCompactBalanceState {}

export type PlayLogCompactBaselineSource =
  | "fresh"
  | "loaded-save"
  | "imported-save"
  | "unknown";

export type PlayLogCompactMHovExecuteSource = PlayLogMHovExecuteSource;

export type PlayLogCompactNHovExecuteSource = PlayLogMHovExecuteSource;

// Baseline is a small, log-readable identification of the gameplay state the
// session started from. It is intentionally a small subset of the full snapshot
// so the session-start row stays a readable header for downstream analysis.
export interface PlayLogCompactBaseline {
  readonly mp?: PlayLogCompactMpSummary;
  readonly source: PlayLogCompactBaselineSource;
  readonly oHovCount: number;
  readonly hovCore: string;
  readonly hovCoreLog10: number | null;
  readonly acquiredHovSkillIds: readonly string[];
  readonly wallUnlocked: boolean;
  readonly activeWallId: string | null;
  readonly clearedWallIds: readonly string[];
  readonly mpCapacity: number;
  readonly mpAssignedTotal: number;
}

export interface BuildPlayLogCompactBaselineInput {
  readonly snapshot?: PlayLogSnapshotSummary;
  readonly sourceHint?: PlayLogCompactBaselineSource;
}

export function buildPlayLogCompactBaseline(
  input: BuildPlayLogCompactBaselineInput,
): PlayLogCompactBaseline {
  const snapshot = input.snapshot;
  const oHovCount = snapshot?.oHovCount ?? 0;
  const hovCore = snapshot?.hovCore ?? "0";
  const acquiredHovSkillIds = snapshot?.hovSkill?.acquiredIds ?? [];
  const wall = snapshot?.wall;
  const wallUnlocked = wall?.unlocked ?? false;
  const activeWallId = wall?.activeWallId ?? null;
  const clearedWallIds = wall?.clearedWallIds ?? [];
  const mp = snapshot?.mp;
  const mpCapacity = mp?.capacity ?? 0;
  const mpAssignedTotal = mp?.assignedTotal ?? 0;

  const looksFresh =
    oHovCount === 0 &&
    hovCore === "0" &&
    acquiredHovSkillIds.length === 0 &&
    activeWallId === null &&
    clearedWallIds.length === 0;
  const source = input.sourceHint ?? (looksFresh ? "fresh" : "loaded-save");

  return {
    source,
    oHovCount,
    hovCore,
    hovCoreLog10: parseHovCoreLog10(hovCore),
    acquiredHovSkillIds: [...acquiredHovSkillIds],
    wallUnlocked,
    activeWallId,
    clearedWallIds: [...clearedWallIds],
    mpCapacity,
    mpAssignedTotal,
    ...(mp !== undefined ? { mp: compactMpSummary(mp) } : {}),
  };
}

export interface PlayLogCompactSessionStartEntry {
  readonly kind: "session-start";
  readonly type: "session-start";
  readonly runId: string;
  readonly seq: number;
  readonly startedAtMs: number;
  readonly initialSpeedMultiplier: number;
  readonly label?: string;
  readonly purpose?: string;
  readonly baseline?: PlayLogCompactBaseline;
}

export interface PlayLogCompactSessionEndEntry {
  readonly kind: "session-end";
  readonly type: "session-end";
  readonly runId: string;
  readonly seq: number;
  readonly endedAtMs: number;
}

export interface PlayLogCompactSampleEntry extends PlayLogCompactCommonFields {
  readonly kind: "sample";
  readonly type: "balance-sample";
}

export interface PlayLogCompactSkillAcquireEntry extends PlayLogCompactCommonFields {
  readonly kind: "event";
  readonly type: "hov-skill-acquire";
  readonly acquiredIds: readonly string[];
  readonly acquiredCountAfter: number;
}

export interface PlayLogCompactMHovExecuteEntry extends PlayLogCompactCommonFields {
  readonly kind: "event";
  readonly type: "mhov-execute";
  readonly ok: boolean;
  readonly executionSource: PlayLogCompactMHovExecuteSource;
  readonly hovBeforeLog10: number | null;
  readonly hovGainPerSecondBeforeLog10: number | null;
  readonly mHovMultiplierBefore: string;
  readonly mHovMultiplierAfter: string;
  readonly mHovMultiplierBeforeLog10: number | null;
  readonly mHovMultiplierAfterLog10: number | null;
  readonly mHovCountBefore: number;
  readonly mHovCountAfter: number;
}

export interface PlayLogCompactNHovExecuteEntry extends PlayLogCompactCommonFields {
  readonly kind: "event";
  readonly type: "nhov-execute";
  readonly ok: boolean;
  readonly executionSource: PlayLogCompactNHovExecuteSource;
  readonly branchId: string;
  readonly hovBeforeLog10: number | null;
  readonly branchExperienceGainedLog10: number | null;
  readonly branchLevelBefore: number;
  readonly branchLevelAfter: number;
  readonly branchLevelGain: number;
  readonly nHovCountBefore: number;
  readonly nHovCountAfter: number;
  readonly nHovBranchLevelsBefore: Record<string, number>;
}

export interface PlayLogCompactOHovCycleSummaryEntry extends PlayLogCompactTimedFields {
  readonly kind: "event";
  readonly type: "ohov-cycle-summary";
  readonly fromOHov: number;
  readonly toOHov: number;
  readonly startGameTimeMs: number | null;
  readonly endGameTimeMs: number;
  readonly durationGameMs: number | null;
  readonly startWallTimeMs: number | null;
  readonly endWallTimeMs: number;
  readonly durationWallMs: number | null;
  readonly peakHovLog10: number | null;
  readonly peakHovGainPerSecondLog10: number | null;
  readonly mHovExecutions: number;
  readonly nHovExecutions: number;
  readonly nHovBranchExecutions: Record<string, number>;
  readonly nHovBranchLevelGains: Record<string, number>;
  readonly hguPurchaseCount: number;
  readonly hguAttempts: number;
  readonly maxHgEnhancementLevel: number | null;
  readonly maxH9gEnhancementLevel: number | null;
  readonly amplifier: {
    readonly availableEver: boolean;
    readonly peakApLog10: number | null;
    readonly peakHgMultiplierLog10: number | null;
    readonly activeEntryIdsEver: readonly string[];
  };
}

// Compact-side execution source. Mirrors PlayLogOHovExecuteSource so detailed
// PlayLog and compact analysis stay aligned. "unknown" preserves backwards
// compatibility for restored entries that pre-date the source field.
export type PlayLogCompactOHovExecuteSource =
  | "manual"
  | "mp"
  | "auto"
  | "wall-start"
  | "unknown";

export interface PlayLogCompactOHovExecuteEntry extends PlayLogCompactCommonFields {
  readonly kind: "event";
  readonly type: "ohov-execute" | "ohov-auto-execute";
  readonly ok?: boolean;
  readonly source?: string;
  readonly executionSource: PlayLogCompactOHovExecuteSource;
  readonly hovBeforeLog10: number | null;
  readonly hovGainPerSecondBeforeLog10: number | null;
  readonly currentLoopHovEarnedLog10: number | null;
  readonly oHovCountBefore: number;
  readonly oHovCountAfter: number;
  readonly hovCoreGain: string;
  readonly hovCoreGainLog10: number | null;
  readonly hovCoreAfter: string;
  readonly hovCoreAfterLog10: number | null;
  readonly runGameDurationMs: number;
  readonly runWallDurationMs: number;
  readonly hovSkillBefore?: PlayLogCompactHovSkillSummary;
  readonly amplifierBefore?: PlayLogCompactAmplifierSummary;
  readonly wallBefore?: PlayLogCompactWallSummary;
  readonly mpBefore?: PlayLogCompactMpSummary;
  readonly nHovBranchLevelsBefore: Record<string, number>;
  readonly mHovCountBefore: number;
  readonly mHovMultiplierBefore: string;
  readonly mHovMultiplierBeforeLog10: number | null;
  readonly nHovCountBefore: number;
  readonly hgEnhancementLevelBefore: number;
  readonly h9gEnhancementLevelBefore?: number;
}

export interface PlayLogCompactWallClearSummaryEntry extends PlayLogCompactCommonFields {
  readonly kind: "event";
  readonly type: "wall-clear-summary";
  readonly wallId: string;
  readonly triggerEventType: "ohov-execute" | "ohov-auto-execute";
  readonly source?: string;
  readonly clearElapsedMs: number | null;
  readonly previousBestClearMs: number | null;
  readonly bestClearMs: number | null;
  readonly rewardMultiplierBefore: number;
  readonly rewardMultiplierAfter: number;
  // Typed reward effect snapshots taken before/after the clear. Preserves the
  // additive semantics for `add`-operation walls (e.g. Freshness Wall MHov
  // exponent bonus) that the multiplier projection cannot carry.
  readonly rewardEffectBefore: WallRewardEffect | null;
  readonly rewardEffectAfter: WallRewardEffect | null;
  readonly firstClear: boolean;
  readonly firstClearReward: WallFirstClearReward | null;
  readonly firstClearHovCoreReward: string | null;
  readonly firstClearHovCoreRewardLog10: number | null;
  readonly oHovCountBefore: number;
  readonly oHovCountAfter: number;
  readonly ohovHovCoreGain: string;
  readonly ohovHovCoreGainLog10: number | null;
  readonly totalHovCoreGain: string;
  readonly totalHovCoreGainLog10: number | null;
  readonly hovCoreAfter: string;
  readonly hovCoreAfterLog10: number | null;
  readonly activeWallIdBefore: string | null;
  readonly clearedWallIdsAfter: readonly string[];
}

export interface PlayLogCompactAchievementUnlockEntry extends PlayLogCompactCommonFields {
  readonly kind: "event";
  readonly type: "achievement-unlock";
  readonly ids: readonly string[];
}

export interface PlayLogCompactSpeedChangeEntry extends PlayLogCompactCommonFields {
  readonly kind: "event";
  readonly type: "speed-change";
  readonly from: number;
  readonly to: number;
}

export interface PlayLogCompactOfflineRewardEntry extends PlayLogCompactCommonFields {
  readonly kind: "event";
  readonly type: "offline-reward-apply";
  readonly source: string;
  readonly realElapsedMs: number;
  readonly uncappedGameElapsedMs: number;
  readonly gameElapsedMs: number;
  readonly wasCapped: boolean;
  readonly rewardId: string;
  readonly rewardFormulaVersion: string;
  readonly timGained: string;
  readonly resultDigest: string;
  readonly commitRevision: number;
  readonly inputPreparationMs: number;
  readonly mainThreadSingleTaskMs: number;
  readonly mainThreadTotalBlockingMs: number;
  readonly endToEndMs: number;
}

export interface PlayLogCompactMpmNumericSettingsChangeEntry extends PlayLogCompactCommonFields {
  readonly kind: "event";
  readonly type: "mpm-numeric-settings-change";
  readonly changes: readonly MpmNumericSettingChange<string | number>[];
}

export type PlayLogCompactEntry =
  | PlayLogCompactMpmNumericSettingsChangeEntry
  | PlayLogCompactSessionStartEntry
  | PlayLogCompactSessionEndEntry
  | PlayLogCompactSampleEntry
  | PlayLogCompactSkillAcquireEntry
  | PlayLogCompactMHovExecuteEntry
  | PlayLogCompactNHovExecuteEntry
  | PlayLogCompactOHovCycleSummaryEntry
  | PlayLogCompactOHovExecuteEntry
  | PlayLogCompactWallClearSummaryEntry
  | PlayLogCompactAchievementUnlockEntry
  | PlayLogCompactSpeedChangeEntry
  | PlayLogCompactOfflineRewardEntry;

export interface BuildPlayLogCompactInput {
  readonly session?: PlayLogSession;
  readonly entries: readonly PlayLogEntry[];
  readonly sessionEnd?: PlayLogSessionEndEntry;
  readonly baseline?: PlayLogCompactBaseline;
}

interface CompactBuilderState {
  // Tracks acquired Hov skills seen so far across the run, so the builder can
  // synthesize hov-skill-acquire entries when new ids appear in a snapshot.
  readonly acquiredHovSkillIds: Set<string>;
  readonly oHovCycle: CompactOHovCycleAccumulator;
  // Compact-side sequence number. Distinct from the detailed `seq` so callers
  // can correlate compact rows without leaking holes from filtered entries.
  nextCompactSeq: number;
}

interface CompactOHovCycleAccumulator {
  fromOHov: number | null;
  startGameTimeMs: number | null;
  startWallTimeMs: number | null;
  peakHovLog10: number | null;
  peakHovGainPerSecondLog10: number | null;
  mHovExecutions: number;
  nHovExecutions: number;
  nHovBranchExecutions: Record<string, number>;
  nHovBranchLevelGains: Record<string, number>;
  hguPurchaseCount: number;
  hguAttempts: number;
  maxHgEnhancementLevel: number | null;
  maxH9gEnhancementLevel: number | null;
  amplifierAvailableEver: boolean;
  amplifierPeakApLog10: number | null;
  amplifierPeakHgMultiplierLog10: number | null;
  amplifierActiveEntryIdsEver: Set<string>;
}

export function buildPlayLogCompactEntries(
  input: BuildPlayLogCompactInput,
): readonly PlayLogCompactEntry[] {
  const out: PlayLogCompactEntry[] = [];
  const state: CompactBuilderState = {
    acquiredHovSkillIds: new Set<string>(
      input.baseline?.acquiredHovSkillIds ?? [],
    ),
    oHovCycle: createOHovCycleAccumulator({
      fromOHov: null,
      startGameTimeMs: input.session === undefined ? null : 0,
      startWallTimeMs: input.session === undefined ? null : 0,
    }),
    nextCompactSeq: 1,
  };

  if (input.session !== undefined) {
    out.push(buildSessionStart(input.session, state, input.baseline));
  }

  for (const entry of input.entries) {
    const compactEntries = compactizeEntry(entry, state);
    out.push(...compactEntries);
  }

  if (input.sessionEnd !== undefined) {
    out.push(buildSessionEnd(input.sessionEnd, state));
  }

  return out;
}

export function exportPlayLogCompactJsonLines(
  input: BuildPlayLogCompactInput,
): string {
  const entries = buildPlayLogCompactEntries(input);
  if (entries.length === 0) {
    return "";
  }
  return entries.map((entry) => JSON.stringify(entry)).join("\n");
}

function compactizeEntry(
  entry: PlayLogEntry,
  state: CompactBuilderState,
): PlayLogCompactEntry[] {
  if (entry.kind === "sample") {
    return compactizeSample(entry, state);
  }
  return compactizeEvent(entry, state);
}

function compactizeSample(
  sample: PlayLogSample,
  state: CompactBuilderState,
): PlayLogCompactEntry[] {
  const result: PlayLogCompactEntry[] = [];
  const balanceState = balanceStateFromSnapshot(sample.snapshot);
  const skillAcquire = detectSkillAcquire(sample.snapshot, sample, state);
  if (skillAcquire !== undefined) {
    result.push(skillAcquire);
  }
  result.push({
    kind: "sample",
    type: "balance-sample",
    runId: sample.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: sample.wallTimeMs,
    gameTimeMs: sample.gameTimeMs,
    speedMultiplier: sample.speedMultiplier,
    ...balanceState,
  });
  observeOHovCycleBalanceState(state.oHovCycle, balanceState);
  return result;
}

function compactizeEvent(
  event: PlayLogEvent,
  state: CompactBuilderState,
): PlayLogCompactEntry[] {
  const isAutomationBurst = event.type === "gameplay-automation-burst-summary";
  if (!isAutomationBurst && !COMPACT_EVENT_PASS_THROUGH.has(event.type)) {
    return [];
  }

  const result: PlayLogCompactEntry[] = [];
  if (event.snapshot !== undefined) {
    const skillAcquire = detectSkillAcquire(event.snapshot, event, state);
    if (skillAcquire !== undefined) {
      result.push(skillAcquire);
    }
  }

  if (isAutomationBurst) {
    observeOHovCycleGameplayAutomationBurst(state.oHovCycle, event);
    return result;
  }

  switch (event.type) {
    case "mhov-execute": {
      const compact = compactizeMHovExecute(event, state);
      result.push(compact);
      observeOHovCycleMHovExecute(state.oHovCycle, compact);
      observeOHovCycleResetBeforePayload(state.oHovCycle, event.payload);
      break;
    }
    case "nhov-execute": {
      const compact = compactizeNHovExecute(event, state);
      result.push(compact);
      observeOHovCycleNHovExecute(state.oHovCycle, compact);
      observeOHovCycleResetBeforePayload(state.oHovCycle, event.payload);
      break;
    }
    case "ohov-execute":
    case "ohov-auto-execute": {
      const summary = compactizeOHovCycleSummary(event, state);
      const compactOHovExecute = compactizeOHovExecute(event, state);
      const wallClearSummary = compactizeWallClearSummary(
        event,
        state,
        compactOHovExecute,
      );
      result.push(summary);
      result.push(compactOHovExecute);
      if (wallClearSummary !== undefined) {
        result.push(wallClearSummary);
      }
      resetOHovCycleAfterExecute(state.oHovCycle, event, summary.toOHov);
      observeOHovCycleBalanceState(
        state.oHovCycle,
        balanceStateFromSnapshot(event.snapshot),
      );
      break;
    }
    case "achievement-unlock": {
      const compact = compactizeAchievementUnlock(event, state);
      result.push(compact);
      observeOHovCycleBalanceState(state.oHovCycle, compact);
      break;
    }
    case "mpm-numeric-settings-change": {
      result.push({
        kind: "event",
        type: "mpm-numeric-settings-change",
        runId: event.runId,
        seq: takeCompactSeq(state),
        wallTimeMs: event.wallTimeMs,
        gameTimeMs: event.gameTimeMs,
        speedMultiplier: event.speedMultiplier,
        ...balanceStateFromSnapshot(event.snapshot),
        changes: (event.payload?.changes ??
          []) as readonly MpmNumericSettingChange<string | number>[],
      });
      break;
    }
    case "speed-change": {
      const compact = compactizeSpeedChange(event, state);
      result.push(compact);
      observeOHovCycleBalanceState(state.oHovCycle, compact);
      break;
    }
    case "offline-reward-apply": {
      const compact = compactizeOfflineReward(event, state);
      result.push(compact);
      observeOHovCycleBalanceState(state.oHovCycle, compact);
      break;
    }
  }

  return result;
}

function observeOHovCycleGameplayAutomationBurst(
  cycle: CompactOHovCycleAccumulator,
  event: PlayLogEvent,
): void {
  const payload = event.payload ?? {};
  cycle.mHovExecutions += readNumber(payload.mHovCount) ?? 0;
  cycle.nHovExecutions += readNumber(payload.nHovCount) ?? 0;
  cycle.hguPurchaseCount += readNumber(payload.hguPurchaseCount) ?? 0;
  cycle.hguAttempts += readNumber(payload.hguAttempts) ?? 0;
  for (const [branchId, count] of Object.entries(
    readNumberRecord(payload.nHovCountsByBranch),
  )) {
    cycle.nHovBranchExecutions[branchId] =
      (cycle.nHovBranchExecutions[branchId] ?? 0) + count;
  }
  for (const [branchId, gain] of Object.entries(
    readNumberRecord(payload.nHovBranchLevelGains),
  )) {
    cycle.nHovBranchLevelGains[branchId] =
      (cycle.nHovBranchLevelGains[branchId] ?? 0) + gain;
  }
  cycle.peakHovLog10 = maxNullableNumber(
    cycle.peakHovLog10,
    readNullableNumber(payload.peakHovLog10),
  );
  cycle.peakHovGainPerSecondLog10 = maxNullableNumber(
    cycle.peakHovGainPerSecondLog10,
    readNullableNumber(payload.peakHovGainPerSecondLog10),
  );
  observeOHovCycleBalanceState(cycle, balanceStateFromSnapshot(event.snapshot));
}

function compactizeMHovExecute(
  event: PlayLogEvent,
  state: CompactBuilderState,
): PlayLogCompactMHovExecuteEntry {
  const payload = event.payload ?? {};
  const mHovMultiplierBefore = readString(payload.mHovMultiplierBefore) ?? "0";
  const mHovMultiplierAfter = readString(payload.mHovMultiplierAfter) ?? "0";
  return {
    kind: "event",
    type: "mhov-execute",
    runId: event.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: event.wallTimeMs,
    gameTimeMs: event.gameTimeMs,
    speedMultiplier: event.speedMultiplier,
    ...balanceStateFromSnapshot(event.snapshot),
    ok: readBoolean(payload.ok) ?? false,
    executionSource: readMHovExecuteSource(payload.executionSource),
    hovBeforeLog10: readNullableNumber(payload.hovBeforeLog10),
    hovGainPerSecondBeforeLog10: readNullableNumber(
      payload.hovGainPerSecondBeforeLog10,
    ),
    mHovMultiplierBefore,
    mHovMultiplierAfter,
    mHovMultiplierBeforeLog10: readNullableNumber(
      payload.mHovMultiplierBeforeLog10,
    ),
    mHovMultiplierAfterLog10: readNullableNumber(
      payload.mHovMultiplierAfterLog10,
    ),
    mHovCountBefore: readNumber(payload.mHovCountBefore) ?? 0,
    mHovCountAfter: readNumber(payload.mHovCountAfter) ?? 0,
  };
}

function compactizeNHovExecute(
  event: PlayLogEvent,
  state: CompactBuilderState,
): PlayLogCompactNHovExecuteEntry {
  const payload = event.payload ?? {};
  const branchLevelBefore = readNumber(payload.branchLevelBefore) ?? 0;
  const branchLevelAfter = readNumber(payload.branchLevelAfter) ?? 0;
  return {
    kind: "event",
    type: "nhov-execute",
    runId: event.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: event.wallTimeMs,
    gameTimeMs: event.gameTimeMs,
    speedMultiplier: event.speedMultiplier,
    ...balanceStateFromSnapshot(event.snapshot),
    ok: readBoolean(payload.ok) ?? false,
    executionSource: readMHovExecuteSource(payload.executionSource),
    branchId: readString(payload.branchId) ?? "",
    hovBeforeLog10: readNullableNumber(payload.hovBeforeLog10),
    branchExperienceGainedLog10: readNullableNumber(
      payload.branchExperienceGainedLog10,
    ),
    branchLevelBefore,
    branchLevelAfter,
    branchLevelGain: branchLevelAfter - branchLevelBefore,
    nHovCountBefore: readNumber(payload.nHovCountBefore) ?? 0,
    nHovCountAfter: readNumber(payload.nHovCountAfter) ?? 0,
    nHovBranchLevelsBefore: readNumberRecord(payload.nHovBranchLevelsBefore),
  };
}

function compactizeOHovCycleSummary(
  event: PlayLogEvent,
  state: CompactBuilderState,
): PlayLogCompactOHovCycleSummaryEntry {
  const payload = event.payload ?? {};
  observeOHovCycleOHovBeforeState(state.oHovCycle, payload);

  const runGameDurationMs = readNonNegativeNumber(payload.runGameDurationMs);
  const runWallDurationMs = readNonNegativeNumber(payload.runWallDurationMs);
  const startGameTimeMs =
    state.oHovCycle.startGameTimeMs ??
    deriveStartTimeMs(event.gameTimeMs, runGameDurationMs);
  const startWallTimeMs =
    state.oHovCycle.startWallTimeMs ??
    deriveStartTimeMs(event.wallTimeMs, runWallDurationMs);

  const fromOHov =
    readNumber(payload.oHovCountBefore) ?? state.oHovCycle.fromOHov ?? 0;
  const toOHov = readNumber(payload.oHovCountAfter) ?? fromOHov;

  return {
    kind: "event",
    type: "ohov-cycle-summary",
    runId: event.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: event.wallTimeMs,
    gameTimeMs: event.gameTimeMs,
    speedMultiplier: event.speedMultiplier,
    fromOHov,
    toOHov,
    startGameTimeMs,
    endGameTimeMs: event.gameTimeMs,
    durationGameMs: resolveDurationMs(
      startGameTimeMs,
      event.gameTimeMs,
      runGameDurationMs,
    ),
    startWallTimeMs,
    endWallTimeMs: event.wallTimeMs,
    durationWallMs: resolveDurationMs(
      startWallTimeMs,
      event.wallTimeMs,
      runWallDurationMs,
    ),
    peakHovLog10: state.oHovCycle.peakHovLog10,
    peakHovGainPerSecondLog10: state.oHovCycle.peakHovGainPerSecondLog10,
    mHovExecutions: state.oHovCycle.mHovExecutions,
    nHovExecutions: state.oHovCycle.nHovExecutions,
    nHovBranchExecutions: {
      ...state.oHovCycle.nHovBranchExecutions,
    },
    nHovBranchLevelGains: {
      ...state.oHovCycle.nHovBranchLevelGains,
    },
    hguPurchaseCount: state.oHovCycle.hguPurchaseCount,
    hguAttempts: state.oHovCycle.hguAttempts,
    maxHgEnhancementLevel: state.oHovCycle.maxHgEnhancementLevel,
    maxH9gEnhancementLevel: state.oHovCycle.maxH9gEnhancementLevel,
    amplifier: {
      availableEver: state.oHovCycle.amplifierAvailableEver,
      peakApLog10: state.oHovCycle.amplifierPeakApLog10,
      peakHgMultiplierLog10: state.oHovCycle.amplifierPeakHgMultiplierLog10,
      activeEntryIdsEver: [...state.oHovCycle.amplifierActiveEntryIdsEver],
    },
  };
}

function compactizeOHovExecute(
  event: PlayLogEvent,
  state: CompactBuilderState,
): PlayLogCompactOHovExecuteEntry {
  const payload = event.payload ?? {};
  const hovCoreGain = readString(payload.hovCoreGain) ?? "0";
  const snapshot = event.snapshot;
  const hovCoreAfter = snapshot?.hovCore ?? "0";
  const hovSkillBefore = parsePlayLogHovSkillSummary(payload.hovSkillBefore);
  const amplifierBeforeAvailable =
    hovSkillBefore?.effects.amplifierUnlocked ?? false;
  const amplifierBefore = parsePlayLogAmplifierSummary(payload.amplifierBefore);
  return {
    kind: "event",
    type:
      event.type === "ohov-auto-execute" ? "ohov-auto-execute" : "ohov-execute",
    runId: event.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: event.wallTimeMs,
    gameTimeMs: event.gameTimeMs,
    speedMultiplier: event.speedMultiplier,
    ...balanceStateFromSnapshot(snapshot),
    ...(payload.ok !== undefined
      ? { ok: readBoolean(payload.ok) ?? false }
      : {}),
    ...(payload.source !== undefined
      ? { source: readString(payload.source) ?? "" }
      : {}),
    executionSource: readOHovExecuteSource(payload.executionSource, event.type),
    hovBeforeLog10: readNullableNumber(payload.hovBeforeLog10),
    hovGainPerSecondBeforeLog10: readNullableNumber(
      payload.hovGainPerSecondBeforeLog10,
    ),
    currentLoopHovEarnedLog10: readNullableNumber(
      payload.currentLoopHovEarnedLog10,
    ),
    oHovCountBefore: readNumber(payload.oHovCountBefore) ?? 0,
    oHovCountAfter: readNumber(payload.oHovCountAfter) ?? 0,
    hovCoreGain,
    hovCoreGainLog10: readNullableNumber(payload.hovCoreGainLog10),
    hovCoreAfter,
    hovCoreAfterLog10: parseHovCoreLog10(hovCoreAfter),
    runGameDurationMs: readNumber(payload.runGameDurationMs) ?? 0,
    runWallDurationMs: readNumber(payload.runWallDurationMs) ?? 0,
    ...(hovSkillBefore !== undefined
      ? { hovSkillBefore: compactHovSkillSummary(hovSkillBefore) }
      : {}),
    ...(amplifierBefore !== undefined
      ? {
          amplifierBefore: compactAmplifierSummary(
            amplifierBefore,
            amplifierBeforeAvailable,
          ),
        }
      : {}),
    ...maybeWallBefore(payload.wallBefore),
    ...maybeMpBefore(payload.mpBefore),
    nHovBranchLevelsBefore: readNumberRecord(payload.nHovBranchLevelsBefore),
    mHovCountBefore: readNumber(payload.mHovCountBefore) ?? 0,
    mHovMultiplierBefore: readString(payload.mHovMultiplierBefore) ?? "0",
    mHovMultiplierBeforeLog10: readNullableNumber(
      payload.mHovMultiplierBeforeLog10,
    ),
    nHovCountBefore: readNumber(payload.nHovCountBefore) ?? 0,
    hgEnhancementLevelBefore: readNumber(payload.hgEnhancementLevelBefore) ?? 0,
    ...(payload.h9gEnhancementLevelBefore !== undefined
      ? {
          h9gEnhancementLevelBefore: readNumber(
            payload.h9gEnhancementLevelBefore,
          ),
        }
      : {}),
  };
}

function compactizeWallClearSummary(
  event: PlayLogEvent,
  state: CompactBuilderState,
  oHovEntry: PlayLogCompactOHovExecuteEntry,
): PlayLogCompactWallClearSummaryEntry | undefined {
  const payload = event.payload ?? {};
  const wallBefore = parsePlayLogWallSummary(payload.wallBefore);
  const wallAfter = parsePlayLogWallSummary(event.snapshot?.wall);
  if (wallBefore === undefined || wallAfter === undefined) {
    return undefined;
  }

  const activeWallIdBefore = wallBefore.activeWallId;
  if (activeWallIdBefore === null) {
    return undefined;
  }
  if (wallAfter.activeWallId !== null) return undefined;
  if (!wallAfter.clearedWallIds.includes(activeWallIdBefore)) {
    return undefined;
  }

  const wallBeforeEntry = wallBefore.walls.find(
    (wall) => wall.id === activeWallIdBefore,
  );
  const wallAfterEntry = wallAfter.walls.find(
    (wall) => wall.id === activeWallIdBefore,
  );
  const previousBestClearMs = wallBeforeEntry?.bestClearMs ?? null;
  const bestClearMs = wallAfterEntry?.bestClearMs ?? null;
  const firstClear = previousBestClearMs === null;
  const firstClearReward = resolveFirstClearReward(
    activeWallIdBefore,
    firstClear,
  );
  const firstClearHovCoreReward =
    firstClearReward?.kind === "hov-core"
      ? serializeHuge(huge(firstClearReward.amount))
      : null;
  const ohovHovCoreGain = deriveOHovEquivalentHovCoreGain(
    oHovEntry.hovCoreGain,
    firstClearHovCoreReward,
  );
  const source = readString(payload.source);

  return {
    kind: "event",
    type: "wall-clear-summary",
    runId: event.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: event.wallTimeMs,
    gameTimeMs: event.gameTimeMs,
    speedMultiplier: event.speedMultiplier,
    ...balanceStateFromSnapshot(event.snapshot),
    wallId: activeWallIdBefore,
    triggerEventType:
      event.type === "ohov-auto-execute" ? "ohov-auto-execute" : "ohov-execute",
    ...(source !== undefined ? { source } : {}),
    clearElapsedMs: wallBefore.activeRunElapsedMs,
    previousBestClearMs,
    bestClearMs,
    rewardMultiplierBefore: wallBeforeEntry?.rewardMultiplier ?? 1,
    rewardMultiplierAfter: wallAfterEntry?.rewardMultiplier ?? 1,
    rewardEffectBefore: wallBeforeEntry?.rewardEffect ?? null,
    rewardEffectAfter: wallAfterEntry?.rewardEffect ?? null,
    firstClear,
    firstClearReward,
    firstClearHovCoreReward,
    firstClearHovCoreRewardLog10:
      firstClearHovCoreReward === null
        ? null
        : parseHovCoreLog10(firstClearHovCoreReward),
    oHovCountBefore: oHovEntry.oHovCountBefore,
    oHovCountAfter: oHovEntry.oHovCountAfter,
    ohovHovCoreGain,
    ohovHovCoreGainLog10: parseHovCoreLog10(ohovHovCoreGain),
    totalHovCoreGain: oHovEntry.hovCoreGain,
    totalHovCoreGainLog10: oHovEntry.hovCoreGainLog10,
    hovCoreAfter: oHovEntry.hovCoreAfter,
    hovCoreAfterLog10: oHovEntry.hovCoreAfterLog10,
    activeWallIdBefore,
    clearedWallIdsAfter: [...wallAfter.clearedWallIds],
  };
}

function resolveFirstClearReward(
  wallId: string,
  firstClear: boolean,
): WallFirstClearReward | null {
  if (!firstClear) {
    return null;
  }
  if (!isWallId(wallId)) {
    return null;
  }
  return getWallFirstClearReward(wallId);
}

function deriveOHovEquivalentHovCoreGain(
  totalHovCoreGain: string,
  firstClearHovCoreReward: string | null,
): string {
  // Compact-only split: detailed OHov payload carries total HovCore gain, so
  // this derives the OHov base-reward-equivalent portion for analysis.
  const total = deserializeHuge(totalHovCoreGain);
  const firstClearReward =
    firstClearHovCoreReward === null
      ? hugeZero()
      : deserializeHuge(firstClearHovCoreReward);
  const derived = total.sub(firstClearReward);
  return serializeHuge(compareHuge(derived, 0) < 0 ? hugeZero() : derived);
}

function compactizeAchievementUnlock(
  event: PlayLogEvent,
  state: CompactBuilderState,
): PlayLogCompactAchievementUnlockEntry {
  const payload = event.payload ?? {};
  const ids = Array.isArray(payload.ids)
    ? payload.ids.filter((id): id is string => typeof id === "string")
    : [];
  return {
    kind: "event",
    type: "achievement-unlock",
    runId: event.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: event.wallTimeMs,
    gameTimeMs: event.gameTimeMs,
    speedMultiplier: event.speedMultiplier,
    ...balanceStateFromSnapshot(event.snapshot),
    ids,
  };
}

function compactizeSpeedChange(
  event: PlayLogEvent,
  state: CompactBuilderState,
): PlayLogCompactSpeedChangeEntry {
  const payload = event.payload ?? {};
  return {
    kind: "event",
    type: "speed-change",
    runId: event.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: event.wallTimeMs,
    gameTimeMs: event.gameTimeMs,
    speedMultiplier: event.speedMultiplier,
    ...balanceStateFromSnapshot(event.snapshot),
    from: readNumber(payload.from) ?? 0,
    to: readNumber(payload.to) ?? 0,
  };
}

function compactizeOfflineReward(
  event: PlayLogEvent,
  state: CompactBuilderState,
): PlayLogCompactOfflineRewardEntry {
  const payload = event.payload ?? {};
  return {
    kind: "event",
    type: "offline-reward-apply",
    runId: event.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: event.wallTimeMs,
    gameTimeMs: event.gameTimeMs,
    speedMultiplier: event.speedMultiplier,
    ...balanceStateFromSnapshot(event.snapshot),
    source: readString(payload.source) ?? "",
    realElapsedMs: readNumber(payload.realElapsedMs) ?? 0,
    uncappedGameElapsedMs: readNumber(payload.uncappedGameElapsedMs) ?? 0,
    gameElapsedMs: readNumber(payload.gameElapsedMs) ?? 0,
    wasCapped: readBoolean(payload.wasCapped) ?? false,
    rewardId: readString(payload.rewardId) ?? "",
    rewardFormulaVersion: readString(payload.rewardFormulaVersion) ?? "",
    timGained: readString(payload.timGained) ?? "0",
    resultDigest: readString(payload.resultDigest) ?? "",
    commitRevision: readNumber(payload.commitRevision) ?? 0,
    inputPreparationMs: readNumber(payload.inputPreparationMs) ?? 0,
    mainThreadSingleTaskMs: readNumber(payload.mainThreadSingleTaskMs) ?? 0,
    mainThreadTotalBlockingMs:
      readNumber(payload.mainThreadTotalBlockingMs) ?? 0,
    endToEndMs: readNumber(payload.endToEndMs) ?? 0,
  };
}

function detectSkillAcquire(
  snapshot: PlayLogSnapshotSummary | undefined,
  source: PlayLogEntry,
  state: CompactBuilderState,
): PlayLogCompactSkillAcquireEntry | undefined {
  const acquiredIds = snapshot?.hovSkill?.acquiredIds;
  if (acquiredIds === undefined) {
    return undefined;
  }
  const newlyAcquired: string[] = [];
  for (const id of acquiredIds) {
    if (!state.acquiredHovSkillIds.has(id)) {
      newlyAcquired.push(id);
      state.acquiredHovSkillIds.add(id);
    }
  }
  if (newlyAcquired.length === 0) {
    return undefined;
  }
  return {
    kind: "event",
    type: "hov-skill-acquire",
    runId: source.runId,
    seq: takeCompactSeq(state),
    wallTimeMs: source.wallTimeMs,
    gameTimeMs: source.gameTimeMs,
    speedMultiplier: source.speedMultiplier,
    ...balanceStateFromSnapshot(snapshot),
    acquiredIds: newlyAcquired,
    acquiredCountAfter: state.acquiredHovSkillIds.size,
  };
}

function buildSessionStart(
  session: PlayLogSession,
  state: CompactBuilderState,
  baseline: PlayLogCompactBaseline | undefined,
): PlayLogCompactSessionStartEntry {
  return {
    kind: "session-start",
    type: "session-start",
    runId: session.runId,
    seq: takeCompactSeq(state),
    startedAtMs: session.startedAtMs,
    initialSpeedMultiplier: session.initialSpeedMultiplier,
    ...(session.label !== undefined ? { label: session.label } : {}),
    ...(session.purpose !== undefined ? { purpose: session.purpose } : {}),
    ...(baseline !== undefined ? { baseline } : {}),
  };
}

function buildSessionEnd(
  sessionEnd: PlayLogSessionEndEntry,
  state: CompactBuilderState,
): PlayLogCompactSessionEndEntry {
  return {
    kind: "session-end",
    type: "session-end",
    runId: sessionEnd.runId,
    seq: takeCompactSeq(state),
    endedAtMs: sessionEnd.endedAtMs,
  };
}

function createOHovCycleAccumulator(input: {
  readonly fromOHov: number | null;
  readonly startGameTimeMs: number | null;
  readonly startWallTimeMs: number | null;
}): CompactOHovCycleAccumulator {
  return {
    fromOHov: input.fromOHov,
    startGameTimeMs: input.startGameTimeMs,
    startWallTimeMs: input.startWallTimeMs,
    peakHovLog10: null,
    peakHovGainPerSecondLog10: null,
    mHovExecutions: 0,
    nHovExecutions: 0,
    nHovBranchExecutions: {},
    nHovBranchLevelGains: {},
    hguPurchaseCount: 0,
    hguAttempts: 0,
    maxHgEnhancementLevel: null,
    maxH9gEnhancementLevel: null,
    amplifierAvailableEver: false,
    amplifierPeakApLog10: null,
    amplifierPeakHgMultiplierLog10: null,
    amplifierActiveEntryIdsEver: new Set<string>(),
  };
}

function resetOHovCycleAfterExecute(
  accumulator: CompactOHovCycleAccumulator,
  event: PlayLogEvent,
  fromOHov: number,
): void {
  accumulator.fromOHov = fromOHov;
  accumulator.startGameTimeMs = event.gameTimeMs;
  accumulator.startWallTimeMs = event.wallTimeMs;
  accumulator.peakHovLog10 = null;
  accumulator.peakHovGainPerSecondLog10 = null;
  accumulator.mHovExecutions = 0;
  accumulator.nHovExecutions = 0;
  accumulator.nHovBranchExecutions = {};
  accumulator.nHovBranchLevelGains = {};
  accumulator.hguPurchaseCount = 0;
  accumulator.hguAttempts = 0;
  accumulator.maxHgEnhancementLevel = null;
  accumulator.maxH9gEnhancementLevel = null;
  accumulator.amplifierAvailableEver = false;
  accumulator.amplifierPeakApLog10 = null;
  accumulator.amplifierPeakHgMultiplierLog10 = null;
  accumulator.amplifierActiveEntryIdsEver = new Set<string>();
}

function observeOHovCycleBalanceState(
  accumulator: CompactOHovCycleAccumulator,
  balanceState: PlayLogCompactBalanceState,
): void {
  if (accumulator.fromOHov === null && balanceState.oHovCount !== undefined) {
    accumulator.fromOHov = balanceState.oHovCount;
  }
  accumulator.peakHovLog10 = maxNullableNumber(
    accumulator.peakHovLog10,
    balanceState.hovLog10 ?? null,
  );
  accumulator.peakHovGainPerSecondLog10 = maxNullableNumber(
    accumulator.peakHovGainPerSecondLog10,
    balanceState.hovGainPerSecondLog10 ?? null,
  );
  accumulator.maxHgEnhancementLevel = maxNullableNumber(
    accumulator.maxHgEnhancementLevel,
    balanceState.hgEnhancementLevel ?? null,
  );
  accumulator.maxH9gEnhancementLevel = maxNullableNumber(
    accumulator.maxH9gEnhancementLevel,
    balanceState.h9gEnhancementLevel ?? null,
  );
  observeOHovCycleAmplifier(accumulator, balanceState.amplifier);
}

function observeOHovCycleMHovExecute(
  accumulator: CompactOHovCycleAccumulator,
  entry: PlayLogCompactMHovExecuteEntry,
): void {
  observeOHovCycleBalanceState(accumulator, entry);
  accumulator.peakHovLog10 = maxNullableNumber(
    accumulator.peakHovLog10,
    entry.hovBeforeLog10,
  );
  accumulator.peakHovGainPerSecondLog10 = maxNullableNumber(
    accumulator.peakHovGainPerSecondLog10,
    entry.hovGainPerSecondBeforeLog10,
  );
  if (entry.ok) {
    accumulator.mHovExecutions += 1;
  }
}

function observeOHovCycleNHovExecute(
  accumulator: CompactOHovCycleAccumulator,
  entry: PlayLogCompactNHovExecuteEntry,
): void {
  observeOHovCycleBalanceState(accumulator, entry);
  accumulator.peakHovLog10 = maxNullableNumber(
    accumulator.peakHovLog10,
    entry.hovBeforeLog10,
  );
  if (entry.ok) {
    accumulator.nHovExecutions += 1;
    addRecordCount(accumulator.nHovBranchExecutions, entry.branchId, 1);
    addRecordCount(
      accumulator.nHovBranchLevelGains,
      entry.branchId,
      Math.max(0, entry.branchLevelGain),
    );
  }
}

function observeOHovCycleResetBeforePayload(
  accumulator: CompactOHovCycleAccumulator,
  payload: Record<string, unknown> | undefined,
): void {
  if (payload === undefined) return;
  accumulator.peakHovLog10 = maxNullableNumber(
    accumulator.peakHovLog10,
    readNullableNumber(payload.hovBeforeLog10),
  );
  accumulator.peakHovGainPerSecondLog10 = maxNullableNumber(
    accumulator.peakHovGainPerSecondLog10,
    readNullableNumber(payload.hovGainPerSecondBeforeLog10),
  );
  accumulator.maxHgEnhancementLevel = maxNullableNumber(
    accumulator.maxHgEnhancementLevel,
    readNumber(payload.hgEnhancementLevelBefore) ?? null,
  );
  accumulator.maxH9gEnhancementLevel = maxNullableNumber(
    accumulator.maxH9gEnhancementLevel,
    readNumber(payload.h9gEnhancementLevelBefore) ?? null,
  );
}

function observeOHovCycleOHovBeforeState(
  accumulator: CompactOHovCycleAccumulator,
  payload: Record<string, unknown>,
): void {
  if (accumulator.fromOHov === null) {
    accumulator.fromOHov = readNumber(payload.oHovCountBefore) ?? null;
  }
  observeOHovCycleResetBeforePayload(accumulator, payload);

  const hovSkillBefore = parsePlayLogHovSkillSummary(payload.hovSkillBefore);
  const amplifierBefore = parsePlayLogAmplifierSummary(payload.amplifierBefore);
  if (amplifierBefore === undefined) return;
  observeOHovCycleAmplifier(
    accumulator,
    compactAmplifierSummary(
      amplifierBefore,
      hovSkillBefore?.effects.amplifierUnlocked ?? false,
    ),
  );
}

function observeOHovCycleAmplifier(
  accumulator: CompactOHovCycleAccumulator,
  amplifier: PlayLogCompactAmplifierSummary | undefined,
): void {
  if (amplifier === undefined || !amplifier.available) {
    return;
  }
  accumulator.amplifierAvailableEver = true;
  accumulator.amplifierPeakApLog10 = maxNullableNumber(
    accumulator.amplifierPeakApLog10,
    amplifier.apLog10,
  );
  accumulator.amplifierPeakHgMultiplierLog10 = maxNullableNumber(
    accumulator.amplifierPeakHgMultiplierLog10,
    amplifier.hgMultiplierLog10,
  );
  for (const id of amplifier.activeEntryIds) {
    accumulator.amplifierActiveEntryIdsEver.add(id);
  }
}

function addRecordCount(
  record: Record<string, number>,
  key: string,
  amount: number,
): void {
  if (key === "" || !Number.isFinite(amount)) return;
  record[key] = (record[key] ?? 0) + amount;
}

function maxNullableNumber(
  current: number | null,
  candidate: number | null,
): number | null {
  if (candidate === null || !Number.isFinite(candidate)) {
    return current;
  }
  if (current === null || candidate > current) {
    return candidate;
  }
  return current;
}

function readNonNegativeNumber(value: unknown): number | null {
  const number = readNumber(value);
  if (number === undefined || number < 0) {
    return null;
  }
  return number;
}

function deriveStartTimeMs(
  endTimeMs: number,
  durationMs: number | null,
): number | null {
  if (durationMs === null) return null;
  const startTimeMs = endTimeMs - durationMs;
  if (!Number.isFinite(startTimeMs) || startTimeMs < 0) return null;
  return startTimeMs;
}

function resolveDurationMs(
  startTimeMs: number | null,
  endTimeMs: number,
  fallbackDurationMs: number | null,
): number | null {
  if (startTimeMs !== null) {
    const durationMs = endTimeMs - startTimeMs;
    if (Number.isFinite(durationMs) && durationMs >= 0) {
      return durationMs;
    }
  }
  return fallbackDurationMs;
}

function balanceStateFromSnapshot(
  snapshot: PlayLogSnapshotSummary | undefined,
): PlayLogCompactBalanceState {
  if (snapshot === undefined) {
    return {};
  }
  const state: Mutable<PlayLogCompactBalanceState> = {};
  if (snapshot.oHovCount !== undefined) state.oHovCount = snapshot.oHovCount;
  if (snapshot.hovLog10 !== undefined) state.hovLog10 = snapshot.hovLog10;
  if (snapshot.hovGainPerSecondLog10 !== undefined)
    state.hovGainPerSecondLog10 = snapshot.hovGainPerSecondLog10;
  if (snapshot.tim !== undefined) state.tim = snapshot.tim;
  if (snapshot.mHovCount !== undefined) state.mHovCount = snapshot.mHovCount;
  if (snapshot.mHovMultiplier !== undefined)
    state.mHovMultiplier = snapshot.mHovMultiplier;
  if (snapshot.mHovMultiplierLog10 !== undefined)
    state.mHovMultiplierLog10 = snapshot.mHovMultiplierLog10;
  if (snapshot.nHovCount !== undefined) state.nHovCount = snapshot.nHovCount;
  if (snapshot.nHovBranchLevels !== undefined)
    state.nHovBranchLevels = { ...snapshot.nHovBranchLevels };
  if (snapshot.hovCore !== undefined) {
    state.hovCore = snapshot.hovCore;
    state.hovCoreLog10 = parseHovCoreLog10(snapshot.hovCore);
  }
  if (snapshot.hgEnhancementLevel !== undefined)
    state.hgEnhancementLevel = snapshot.hgEnhancementLevel;
  if (snapshot.h9gEnhancementLevel !== undefined)
    state.h9gEnhancementLevel = snapshot.h9gEnhancementLevel;
  if (snapshot.hgMilestoneCount !== undefined)
    state.hgMilestoneCount = snapshot.hgMilestoneCount;
  if (snapshot.h9gMilestoneCount !== undefined)
    state.h9gMilestoneCount = snapshot.h9gMilestoneCount;
  if (snapshot.hovSkill !== undefined)
    state.hovSkill = compactHovSkillSummary(snapshot.hovSkill);
  if (snapshot.amplifier !== undefined) {
    const amplifierAvailable =
      snapshot.hovSkill?.effects.amplifierUnlocked ?? false;
    state.amplifier = compactAmplifierSummary(
      snapshot.amplifier,
      amplifierAvailable,
    );
  }
  if (snapshot.wall !== undefined)
    state.wall = compactWallSummary(snapshot.wall);
  if (snapshot.mp !== undefined) state.mp = compactMpSummary(snapshot.mp);
  return state;
}

function compactHovSkillSummary(
  summary: PlayLogHovSkillSummary,
): PlayLogCompactHovSkillSummary {
  const effects = summary.effects;
  return {
    acquiredCount: summary.acquiredCount,
    acquiredIds: [...summary.acquiredIds],
    hov1GainMultiplierLog10: effects.hov1GainMultiplierLog10,
    hovGainPerSecondLog10: effects.hovGainPerSecondLog10,
    timGainPerSecondLog10: effects.timGainPerSecondLog10,
    mpCapacityBonus: effects.mpCapacityBonus,
    amplifierUnlocked: effects.amplifierUnlocked,
    amplifierPanelMpPlacementUnlocked:
      effects.amplifierPanelMpPlacementUnlocked,
    mhovMpExecutionUnlocked: effects.mhovMpExecutionUnlocked,
    nhovMpExecutionAssignmentUnlocked:
      effects.nhovMpExecutionAssignmentUnlocked,
    ohovHovCoreGainMultiplierUnlocked:
      effects.ohovHovCoreGainMultiplierUnlocked,
    mpCapacityCoreOHovHovCoreGainMultiplierUnlocked:
      effects.mpCapacityCoreOHovHovCoreGainMultiplierUnlocked,
    freeMpCoreOHovHovCoreGainMultiplierUnlocked:
      effects.freeMpCoreOHovHovCoreGainMultiplierUnlocked,
    wallUnlocked: effects.wallUnlocked,
    explorationUnlocked: effects.explorationUnlocked ?? false,
    wallRewardCapRelaxed: effects.wallRewardCapRelaxed,
  };
}

function compactAmplifierSummary(
  summary: PlayLogAmplifierSummary,
  amplifierAvailable: boolean,
): PlayLogCompactAmplifierSummary {
  const entries = summary.entries.map(
    (entry): PlayLogCompactAmplifierEntrySummary => ({
      id: entry.id,
      available: amplifierAvailable && entry.unlocked,
      statePresent: true,
      gaugeValue: entry.gaugeValue,
      burnState: entry.burnState,
      amplifierGainMultiplierLog10: entry.amplifierGainMultiplierLog10,
      activeGaugeOutputGainLog10: entry.activeGaugeOutputGainLog10,
    }),
  );
  const availableEntryIds = entries
    .filter((entry) => entry.available)
    .map((entry) => entry.id);
  const availableEntryIdSet = new Set(availableEntryIds);
  const activeEntryIds = summary.activeEntryIds.filter((id) =>
    availableEntryIdSet.has(id),
  );

  return {
    available: amplifierAvailable,
    statePresent: true,
    ap: summary.ap,
    apLog10: summary.apLog10,
    apExponent: summary.apExponent,
    hgMultiplier: summary.hgMultiplier,
    hgMultiplierLog10: summary.hgMultiplierLog10,
    availableEntryIds,
    activeEntryIds: [...activeEntryIds],
    entries,
  };
}

function compactWallSummary(
  summary: PlayLogWallSummary,
): PlayLogCompactWallSummary {
  return {
    unlocked: summary.unlocked,
    activeWallId: summary.activeWallId,
    activeRunElapsedMs: summary.activeRunElapsedMs,
    clearedWallIds: [...summary.clearedWallIds],
    walls: summary.walls.map(
      (wall): PlayLogCompactWallEntrySummary => ({
        id: wall.id,
        bestClearMs: wall.bestClearMs,
        rewardEffect: wall.rewardEffect,
        rewardMultiplier: wall.rewardMultiplier,
      }),
    ),
  };
}

function compactMpSummary(summary: PlayLogMpSummary): PlayLogCompactMpSummary {
  return {
    capacity: summary.capacity,
    assignedTotal: summary.assignedTotal,
    available: summary.available,
    assignments: {
      hovTargets: summary.assignments.hovTargets,
      hovGainUpgrades: summary.assignments.hovGainUpgrades,
      amplifierPanels: summary.assignments.amplifierPanels,
      amplifierUpgrades: summary.assignments.amplifierUpgrades,
      mhovExecutionMpCost: summary.assignments.mhovExecutionMpCost,
      nhovExecutionAssignments: summary.assignments.nhovExecutionAssignments,
      nhovExecutionMpCost: summary.assignments.nhovExecutionMpCost,
      ohovExecutionMpCost: summary.assignments.ohovExecutionMpCost,
    },
    hovTargetCellIndices: [...summary.hovTargetCellIndices],
    hovGainUpgradeTargets: [...summary.hovGainUpgradeTargets],
    amplifierPanelEntryIds: [...summary.amplifierPanelEntryIds],
    amplifierUpgradeEntryIds: [...summary.amplifierUpgradeEntryIds],
    nhovExecutionBranchIds: [...summary.nhovExecutionBranchIds],
    ...(summary.numericSettings !== undefined
      ? { numericSettings: summary.numericSettings }
      : {}),
  };
}

function maybeWallBefore(
  value: unknown,
): { wallBefore: PlayLogCompactWallSummary } | Record<string, never> {
  const summary = parsePlayLogWallSummary(value);
  if (summary === undefined) return {};
  return { wallBefore: compactWallSummary(summary) };
}

function maybeMpBefore(
  value: unknown,
): { mpBefore: PlayLogCompactMpSummary } | Record<string, never> {
  const summary = parsePlayLogMpSummary(value);
  if (summary === undefined) return {};
  return { mpBefore: compactMpSummary(summary) };
}

// Event payloads are typed as `Record<string, unknown>` on the detailed entry,
// so before-state balance summaries arrive as `unknown`. These helpers
// narrow them back to their typed shapes assuming the producer is the runtime
// itself (it always builds these via the balance summary builders).

function parsePlayLogHovSkillSummary(
  value: unknown,
): PlayLogHovSkillSummary | undefined {
  if (!isPlainRecord(value)) return undefined;
  return value as unknown as PlayLogHovSkillSummary;
}

function parsePlayLogAmplifierSummary(
  value: unknown,
): PlayLogAmplifierSummary | undefined {
  if (!isPlainRecord(value)) return undefined;
  return value as unknown as PlayLogAmplifierSummary;
}

function parsePlayLogWallSummary(
  value: unknown,
): PlayLogWallSummary | undefined {
  if (!isPlainRecord(value)) return undefined;
  return value as unknown as PlayLogWallSummary;
}

function parsePlayLogMpSummary(value: unknown): PlayLogMpSummary | undefined {
  if (!isPlainRecord(value)) return undefined;
  return value as unknown as PlayLogMpSummary;
}

function takeCompactSeq(state: CompactBuilderState): number {
  const seq = state.nextCompactSeq;
  state.nextCompactSeq += 1;
  return seq;
}

function readNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return undefined;
}

function readNullableNumber(value: unknown): number | null {
  if (value === null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

function readBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  return undefined;
}

function readString(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  return undefined;
}

function readMHovExecuteSource(
  value: unknown,
): PlayLogCompactMHovExecuteSource {
  const source = readString(value);
  if (source === "manual" || source === "mp-automation") {
    return source;
  }
  return "unknown";
}

function readOHovExecuteSource(
  value: unknown,
  eventType: string,
): PlayLogCompactOHovExecuteSource {
  const source = readString(value);
  if (
    source === "manual" ||
    source === "mp" ||
    source === "auto" ||
    source === "wall-start" ||
    source === "unknown"
  ) {
    return source;
  }
  // Compat path: pre-source PlayLog payloads carried only the event type. The
  // legacy "ohov-execute" was always manual; "ohov-auto-execute" was always
  // forced auto. Fall back to those defaults so historical entries still
  // classify correctly.
  if (eventType === "ohov-auto-execute") return "auto";
  if (eventType === "ohov-execute") return "manual";
  return "unknown";
}

function readNumberRecord(value: unknown): Record<string, number> {
  if (!isPlainRecord(value)) return {};
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(value)) {
    if (typeof v === "number" && Number.isFinite(v)) {
      out[k] = v;
    }
  }
  return out;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function safeLog10(value: number): number | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  const result = Math.log10(value);
  return Number.isFinite(result) ? result : null;
}

function parseHovCoreLog10(serialized: string): number | null {
  const parsed = Number(serialized);
  return safeLog10(parsed);
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

export type PlayLogCompactJsonLineEntry = PlayLogCompactEntry;
